
import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const countryPhoneCodes = [
  { value: '+1', label: 'US/CA (+1)' },
  { value: '+44', label: 'UK (+44)' },
  { value: '+86', label: 'CN (+86)' },
  { value: '+81', label: 'JP (+81)' },
  { value: '+82', label: 'KR (+82)' },
  { value: '+65', label: 'SG (+65)' },
  { value: '+61', label: 'AU (+61)' },
  { value: '+852', label: 'HK (+852)' },
  { value: '+91', label: 'IN (+91)' },
  { value: '+33', label: 'FR (+33)' },
  { value: '+49', label: 'DE (+49)' },
  { value: '+34', label: 'ES (+34)' },
  { value: '+39', label: 'IT (+39)' },
  // Add more as needed
];

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  name?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
  countryCodeName?: string;
  phoneNumberName?: string;
  placeholder?: string;
}

export function PhoneInput({
  value,
  onChange,
  name,
  id,
  className,
  disabled,
  countryCodeName,
  phoneNumberName,
  placeholder = ''
}: PhoneInputProps) {
  // Enhanced parsing to handle various phone number formats
  const [countryCode, localNumber] = React.useMemo(() => {
    if (!value) return ['+1', ''];
    
    // Handle properly formatted numbers like "+1 9099057788"
    const properFormatMatch = value.match(/^(\+\d+)\s+(.*)$/);
    if (properFormatMatch) {
      return [properFormatMatch[1], properFormatMatch[2]];
    }
    
    // Handle numbers that start with + but no space like "+19099057788"
    const nospaceMatch = value.match(/^(\+\d{1,4})(.*)$/);
    if (nospaceMatch) {
      return [nospaceMatch[1], nospaceMatch[2]];
    }
    
    // Handle legacy formats like "19099057788" (assume US/CA)
    if (value.match(/^1\d{10}$/)) {
      return ['+1', value.substring(1)];
    }
    
    // Handle 10-digit US numbers like "9099057788"
    if (value.match(/^\d{10}$/)) {
      return ['+1', value];
    }
    
    // Default fallback
    return ['+1', value];
  }, [value]);

  const handleCountryCodeChange = (newCode: string) => {
    onChange(`${newCode} ${localNumber}`);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value;
    onChange(`${countryCode} ${newNumber}`);
  };

  return (
    <div className={`flex gap-2 ${className || ''}`}>
      <Select value={countryCode} onValueChange={handleCountryCodeChange} disabled={disabled}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="Code" />
        </SelectTrigger>
        <SelectContent>
          {countryPhoneCodes.map((code) => (
            <SelectItem key={code.value} value={code.value}>
              {code.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="tel"
        value={localNumber}
        onChange={handleNumberChange}
        placeholder={placeholder}
        className="flex-1"
        name={name || phoneNumberName}
        id={id}
        disabled={disabled}
        autoComplete="tel"
      />
    </div>
  );
}
